'use client';

/**
 * FlowHeader Component
 *
 * Shared header component for the purchase flow with back and close buttons.
 * Used by PurchaseFlow to provide consistent navigation across all screens.
 *
 * Figma design: Title on the left, close button on the right, aligned to baseline.
 */

import React from 'react';
import { PURCHASE_FLOW_ICONS } from '../utils/constants';
import { usePurchaseRender } from '../config/PurchaseFlowConfigProvider';
import classes from './classes.module.scss';

// ============================================================================
// Types
// ============================================================================

export interface FlowHeaderProps {
  /** Text key for the title (see PURCHASE_FLOW_TEXT_KEYS.TITLE_* constants) */
  titleKey?: string;
  /** Whether to show the back button (left side, before title) */
  showBackButton?: boolean;
  /** Whether to show the close button (right side) */
  showCloseButton?: boolean;
  /** Callback when back button is clicked */
  onBack?: () => void;
  /** Callback when close button is clicked */
  onClose?: () => void;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Header component with optional back and close buttons
 *
 * Layout: [Back Button] [Title ............] [Close Button]
 * - Back button shown before title when showBackButton=true
 * - Close button shown on right when showCloseButton=true
 * - Title uses flex: 1 to take remaining space
 *
 * When only the close button is visible (e.g. processing error screen),
 * renders just the absolutely-positioned button without the .header wrapper
 * so it doesn't take up flex gap space in the parent layout.
 * Returns null when there is nothing to render at all.
 */
export const FlowHeader: React.FC<FlowHeaderProps> = ({
  titleKey,
  showBackButton = false,
  showCloseButton = true,
  onBack,
  onClose
}) => {
  const { renderIcon, renderText } = usePurchaseRender();

  const hasBackButton = showBackButton && onBack;
  const hasCloseButton = showCloseButton && onClose;
  const hasFlowContent = !!(titleKey || hasBackButton);

  // Nothing to render at all
  if (!hasFlowContent && !hasCloseButton) return null;

  // Only the close button (absolutely positioned) — render without wrapper
  // so it doesn't occupy flex gap space in the parent layout
  if (!hasFlowContent && hasCloseButton) {
    return (
      <div className={ classes.headerCloseButtonContainer }>
        <button
          type="button"
          onClick={ onClose }
          aria-label="Close"
          className={ classes.headerButton }
          data-testid="purchase-flow-close-button"
        >
          {renderIcon(PURCHASE_FLOW_ICONS.CLOSE)}
        </button>
      </div>
    );
  }

  return (
    <div className={ classes.header }>
      {/* Back button (shown before title) */}
      {hasBackButton && (
      <div className={ classes.headerBackButtonContainer }>
        <button
          type="button"
          onClick={ onBack }
          aria-label="Back"
          className={ classes.headerBackButton }
          data-testid="purchase-flow-back-button"
        >
          {renderIcon(PURCHASE_FLOW_ICONS.BACK)}
        </button>
      </div>
      )}

      {/* Title */}
      {titleKey && (
      <div className={ classes.headerTitle } data-testid="purchase-flow-header-title">
        {renderText(titleKey)}
      </div>
      )}

      {/* Close button — absolutely positioned to top-right of the card */}
      {hasCloseButton && (
      <div className={ classes.headerCloseButtonContainer }>
        <button
          type="button"
          onClick={ onClose }
          aria-label="Close"
          className={ classes.headerButton }
          data-testid="purchase-flow-close-button"
        >
          {renderIcon(PURCHASE_FLOW_ICONS.CLOSE)}
        </button>
      </div>
      )}
    </div>
  );
};

export default FlowHeader;
